import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { createBrowserRouter, Navigate, Outlet, RouterProvider } from 'react-router-dom'
import { ThemeProvider } from '@mui/material/styles'
import CssBaseline from '@mui/material/CssBaseline'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon'

import theme from './theme'
import Login from './Pages/Login'
import Dashboard from './Pages/Dashboard/Dashboard'
import { Agencies } from './Pages/Agencies/Agencies'
import { AgencyDetails } from './Pages/Agencies/AgencyDetails'
import { Configs } from './Pages/Configs/Configs'
import { Users } from './Pages/Users/Users'
import { Processes } from './Pages/Processes/Processes'
import { Servers } from './Pages/Servers/Servers'
import { LogsPanel } from './Pages/LogsPanel/LogsPanel'
import { RestoredLogsPanel } from './Pages/RestoredLogs/RestoredLogsPanel'
import { Maintenance } from './Pages/Maintenance/Maintenance'
import { Settings } from './Pages/Settings/Settings'

import ProtectedRoute from './Pages/Common/ProtectedRoute'
import AuthProvider, { useAuth } from './Provider/AuthProvider'
import { useState } from 'react'
import { useMount } from 'react-use'
import { FC } from './Services'
import { Background, Flex, Spinner } from './Components'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import ErrorHandler from './Pages/Common/ErrorHandler'

// Inizializza il client di React Query
export const queryClient = new QueryClient()

function AppSetup() {
  const { user: storedUser, onLoginJWT } = useAuth()
  const [loading, setLoading] = useState(true)

  useMount(async () => {
    if (FC.authenticated && !storedUser?.accessToken) {
      setLoading(false)
      return true
    }
    storedUser?.accessToken && await onLoginJWT(storedUser?.accessToken)
    setLoading(false)
  })

  return (
    <Background>
      <ReactQueryDevtools initialIsOpen={false} />
      {loading ? <Flex fw fh><Spinner /></Flex> : <Outlet />}
    </Background>
  )
}

const router = createBrowserRouter([
  {
    element: <AuthProvider />,
    children: [
      {
        element: <AppSetup />,
        children: [
          {
            path: '/',
            element: <ProtectedRoute />, // Rotta protetta generale
            children: [
              { path: '/', element: <Navigate to='/dashboard' /> },
              { path: '/dashboard', element: <Dashboard /> },
              // siapAdmin
              {
                path: '/siapadmin',
                element: <ProtectedRoute roles={['siapAdmin']} />,
                children: [
                  { path: 'agencies', element: <Agencies /> },
                  { path: 'agencies/:agencyId', element: <AgencyDetails /> },
                  { path: 'servers', element: <Servers /> },
                  { path: 'users', element: <Users /> },
                  { path: 'processes', element: <Processes /> },
                  { path: 'configs', element: <Configs /> },
                  { path: 'configs/:agencyId', element: <Configs /> },
                  { path: 'logs', element: <LogsPanel /> },
                  { path: 'restored-logs', element: <RestoredLogsPanel /> },
                  { path: 'maintenance', element: <Maintenance /> },
                  { path: 'settings', element: <Settings /> }
                ]
              },
              // siapDeveloper
              {
                path: '/siapdeveloper',
                element: <ProtectedRoute roles={['siapDeveloper']} />,
                children: [
                  { path: 'agencies', element: <Agencies /> },
                  { path: 'agencies/:agencyId', element: <AgencyDetails /> },
                  { path: 'processes', element: <Processes /> },
                ]
              },
              // agencyAdmin
              {
                path: '/admin',
                element: <ProtectedRoute roles={['agencyAdmin']} />,
                children: [
                  { path: 'users', element: <Users /> },
                  { path: 'processes', element: <Processes /> },
                ]
              },
              { path: '/*', element: <ErrorHandler /> }
            ]
          },
          { path: '/login', element: <Login /> }
        ]
      }
    ]
  }
])

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <LocalizationProvider dateAdapter={AdapterLuxon} adapterLocale='it'>
        <QueryClientProvider client={queryClient}>
          <RouterProvider router={router} />
        </QueryClientProvider>
      </LocalizationProvider>
    </ThemeProvider>
  )
}

export default App
